/**
 * Client-side PDF text extraction using PDF.js (browser).
 * Use this instead of server-side extraction for reliable text from readable PDFs.
 */

// Vite resolves ?url at build time so the worker is bundled
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

const MAX_TEXT_LENGTH = 150_000;

let workerInitialized = false;

async function ensureWorker() {
  if (workerInitialized) return;
  const pdfjsLib = await import("pdfjs-dist");
  const gwo = (pdfjsLib as { GlobalWorkerOptions?: { workerSrc?: string }; version?: string }).GlobalWorkerOptions;
  if (typeof window !== "undefined" && gwo && !gwo.workerSrc) {
    gwo.workerSrc = pdfjsWorkerUrl;
    if (!gwo.workerSrc) {
      gwo.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as { version?: string }).version || "4.0.379"}/pdf.worker.min.mjs`;
    }
    workerInitialized = true;
  }
}

export type PdfSource = File | Blob | ArrayBuffer;

/**
 * Extract text from a PDF using PDF.js in the browser. Returns null if not in browser or extraction fails.
 */
export async function extractPdfTextInBrowser(source: PdfSource): Promise<string | null> {
  if (typeof window === "undefined") return null;
  try {
    await ensureWorker();
    const pdfjsLib = await import("pdfjs-dist");
    const getDocument = (pdfjsLib as { getDocument: (opts: unknown) => Promise<{ promise: Promise<{ numPages: number; getPage: (n: number) => Promise<{ getTextContent: () => Promise<{ items: { str?: string }[] }> }> }> }> }).getDocument;
    const data = source instanceof ArrayBuffer ? new Uint8Array(source) : source;
    const doc = await getDocument({ data }).promise;
    const numPages = doc.numPages;
    const parts: string[] = [];
    for (let i = 1; i <= numPages; i++) {
      const page = await doc.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = (textContent.items as { str?: string }[])
        .map((item) => item.str ?? "")
        .join(" ");
      if (pageText.trim()) parts.push(pageText.trim());
    }
    const text = parts.join("\n\n").trim();
    return text.length > 0 ? text.substring(0, MAX_TEXT_LENGTH) : null;
  } catch (e) {
    console.warn("Client-side PDF extraction failed:", e);
    return null;
  }
}

export function isPdfFile(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase();
  return ext === "pdf" || file.type === "application/pdf";
}
